// Leitura do contato compartilhado (vCard) que chega em Message.body.
//
// Cópia da leitura de backend/dist/helpers/VCard.js (`lerVCards`) — os dois precisam
// concordar, e é lá que estão os testes. A leitura é linha a linha, pelo nome da
// propriedade: a anterior procurava "FN" e "+" em qualquer pedaço do texto, e a foto em
// base64 que o celular embute no cartão tem os dois — o card saía sem nome.

const ROTULOS_TIPO_TEL = {
  CELL: "Celular",
  MOBILE: "Celular",
  IPHONE: "iPhone",
  HOME: "Casa",
  WORK: "Trabalho",
  MAIN: "Principal",
  VOICE: "Telefone",
  FAX: "Fax",
};

const desescapar = (valor) =>
  String(valor || "")
    .replace(/\\n/gi, "\n")
    .replace(/\\([,;\\])/g, "$1")
    .trim();

/// separa por ";" sem quebrar o "\;" escapado ///
const componentes = (valor) => {
  const texto = String(valor || "");
  const partes = [];
  let parte = "";
  for (let i = 0; i < texto.length; i++) {
    if (texto[i] === "\\" && i + 1 < texto.length) {
      parte += texto[i] + texto[i + 1];
      i++;
    } else if (texto[i] === ";") {
      partes.push(parte);
      parte = "";
    } else {
      parte += texto[i];
    }
  }
  partes.push(parte);
  return partes.map(desescapar);
};

const novoContato = () => ({ nome: "", nomeEstruturado: "", telefones: [], emails: [], empresa: "", foto: null });

const nomeFinal = (contato) =>
  contato.nome || contato.nomeEstruturado || contato.empresa || contato.telefones[0]?.numero || "";

export const ehVCard = (texto) => typeof texto === "string" && /BEGIN:VCARD/i.test(texto);

export const lerVCards = (texto) => {
  if (!texto || typeof texto !== "string") return [];

  const linhas = texto.replace(/\r\n?/g, "\n").replace(/\n[ \t]/g, "").split("\n");

  const contatos = [];
  let atual = null;
  let rotulosPorGrupo = {};

  const fechar = () => {
    if (!atual) return;
    for (const tel of atual.telefones) {
      if (!tel.rotulo && tel.grupo && rotulosPorGrupo[tel.grupo]) {
        tel.rotulo = rotulosPorGrupo[tel.grupo];
      }
      delete tel.grupo;
    }
    atual.nome = nomeFinal(atual);
    delete atual.nomeEstruturado;
    if (atual.nome || atual.telefones.length) contatos.push(atual);
    atual = null;
    rotulosPorGrupo = {};
  };

  for (const linha of linhas) {
    const doisPontos = linha.indexOf(":");
    if (doisPontos === -1) continue;

    const cabeca = linha.slice(0, doisPontos);
    const valor = linha.slice(doisPontos + 1);
    const [nomeComGrupo, ...params] = cabeca.split(";");
    const ponto = nomeComGrupo.lastIndexOf(".");
    const grupo = ponto === -1 ? "" : nomeComGrupo.slice(0, ponto).toLowerCase();
    const prop = (ponto === -1 ? nomeComGrupo : nomeComGrupo.slice(ponto + 1)).trim().toUpperCase();

    if (prop === "BEGIN" && valor.trim().toUpperCase() === "VCARD") {
      fechar();
      atual = novoContato();
      continue;
    }
    if (prop === "END" && valor.trim().toUpperCase() === "VCARD") {
      fechar();
      continue;
    }
    if (!atual) atual = novoContato();

    const parametros = params.map((p) => p.trim());
    const parametro = (nome) => {
      const achado = parametros.find((p) => p.toLowerCase().startsWith(nome + "="));
      return achado ? achado.slice(nome.length + 1) : null;
    };

    switch (prop) {
      case "FN":
        atual.nome = desescapar(valor);
        break;
      case "N": {
        const [sobrenome, nome, meio, prefixo, sufixo] = componentes(valor);
        atual.nomeEstruturado = [prefixo, nome, meio, sobrenome, sufixo].filter(Boolean).join(" ");
        break;
      }
      case "X-WA-BIZ-NAME":
        if (!atual.nome) atual.nome = desescapar(valor);
        break;
      case "ORG":
        atual.empresa = componentes(valor).filter(Boolean).join(" - ");
        break;
      case "EMAIL": {
        const email = desescapar(valor);
        if (email) atual.emails.push(email);
        break;
      }
      case "TEL": {
        const numero = desescapar(valor);
        const waid = (parametro("waid") || "").replace(/\D/g, "") || null;
        const digitos = waid || numero.replace(/\D/g, "");
        if (!digitos) break;
        const tipo = (parametro("type") || parametros.find((p) => !p.includes("=")) || "")
          .split(",")[0]
          .toUpperCase();
        atual.telefones.push({ numero, waid, digitos, rotulo: ROTULOS_TIPO_TEL[tipo] || "", grupo });
        break;
      }
      case "X-ABLABEL":
        if (grupo) rotulosPorGrupo[grupo] = desescapar(valor).replace(/^_\$!<(.*)>!\$_$/, "$1");
        break;
      case "PHOTO": {
        const bruto = valor.trim();
        if (/^data:image\/[a-z+]+;base64,[A-Za-z0-9+/=]+$/i.test(bruto)) {
          atual.foto = bruto;
        } else if (
          /^[A-Za-z0-9+/=]+$/.test(bruto) &&
          parametros.some((p) => /^(encoding=)?(b|base64)$/i.test(p))
        ) {
          const formato = (parametro("type") || "jpeg").toLowerCase();
          atual.foto = `data:image/${formato};base64,${bruto}`;
        }
        break;
      }
      default:
        break;
    }
  }
  fechar();
  return contatos;
};

/** "Daniel" ou "Daniel e mais 2 contatos". */
export const descreverContatos = (contatos) => {
  if (!contatos || !contatos.length) return "Contato";
  const primeiro = contatos[0].nome || "Contato";
  if (contatos.length === 1) return primeiro;
  const outros = contatos.length - 1;
  return `${primeiro} e mais ${outros} contato${outros > 1 ? "s" : ""}`;
};

/** Texto curto de um body de contato — notificação, citação, lista antiga. */
export const resumoDoVCard = (texto) => `👤 ${descreverContatos(lerVCards(texto))}`;

/** O telefone do "Conversar": o que tem WhatsApp (waid) antes dos outros. */
export const telefoneParaConversar = (contato) =>
  contato?.telefones?.find((t) => t.waid) || contato?.telefones?.[0] || null;
